import type { ThemeProvider } from '@mui/material/styles';
import type { ComponentProps } from 'react';

type StorageManager = NonNullable<ComponentProps<typeof ThemeProvider>['storageManager']>;

const SAVED_VALUES: readonly string[] = ['light', 'dark'];
const isSavedValue = (value: string | null | undefined): value is string =>
  value !== null && value !== undefined && SAVED_VALUES.includes(value);

/**
 * テーマの保存先(localStorage)。MUI 標準との違いは、保存値を 'light' / 'dark' に限ること。
 * 'system' や不正な値は「保存なし」として扱い、既定(ライト)にする。
 * そうしないと、'system' が入っていたときだけ OS の設定に従ってダークになり、
 * index.html の初回描画(ライト)とも食い違う。保存できない・読めない場合も既定のまま動く。
 */
export const themeModeStorage: StorageManager = ({ key, storageWindow }) => {
  const win = storageWindow ?? (typeof window === 'undefined' ? undefined : window);
  return {
    get: (defaultValue: string): string => {
      try {
        const saved = win?.localStorage.getItem(key);
        return isSavedValue(saved) ? saved : defaultValue;
      } catch {
        return defaultValue;
      }
    },
    set: (value: string): void => {
      try {
        win?.localStorage.setItem(key, value);
      } catch {
        // 保存できなくても、切り替え自体は続ける(次回に引き継げないだけ)
      }
    },
    subscribe: (handler) => {
      if (win === undefined) return () => undefined;
      const listener = (event: StorageEvent): void => {
        if (event.key === key) handler(isSavedValue(event.newValue) ? event.newValue : null);
      };
      win.addEventListener('storage', listener);
      return () => {
        win.removeEventListener('storage', listener);
      };
    },
  };
};
